import React, { useCallback, useEffect } from "react";
import ReactFlow, { MiniMap, Controls, Background, BackgroundVariant, useNodesState, useEdgesState, Connection, Edge, Node, NodeTypes, EdgeTypes, ReactFlowProvider, Panel } from "reactflow";
import "reactflow/dist/style.css";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { Save, Edit2, Eye, HelpCircle, Plus } from "lucide-react";
import toast from "react-hot-toast";

import { ServiceNodeComponent } from "./nodes/ServiceNode";
import { ConvoyNodeComponent } from "./nodes/ConvoyNode";
import { AnimatedEdge } from "./edges/AnimatedEdge";
import { FloatingToolbar } from "./FloatingToolbar";
import { Sidebar } from "./Sidebar";
import { useDiagramStore } from "../store/diagramStore";
import { ServiceNode, ServiceConnection } from "../types/diagram.types";
import { useTheme } from "../context/ThemeContext";
import generateDiagramPrompt from "../prompts/generateDiagramPrompt";

const EditorContainer = styled.div<{ $isDarkMode: boolean; $isEditMode: boolean }>`
  width: 100%;
  height: 100vh;
  position: relative;
  background: ${(props) => (props.$isDarkMode ? "#1a1d23" : "#f8f9fa")};
  transition: background 0.3s ease;
  box-shadow: inset 0 0 0 ${(props) => (props.$isEditMode ? 3 : 0)}px ${(props) => (props.$isDarkMode ? "rgba(94, 114, 228, 0.6)" : "rgba(94, 114, 228, 0.7)")};
`;

const ToolbarButton = styled(motion.button)`
  background: white;
  border: none;
  border-radius: 8px;
  padding: 10px;
  margin: 0 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const HelpPanel = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: ${(props) => (props.$isDarkMode ? "#2c2f36" : "white")};
  color: ${(props) => (props.$isDarkMode ? "#e9ecef" : "#333")};
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  max-width: 200px;

  h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  div {
    margin: 4px 0;
    display: flex;
    justify-content: space-between;
  }

  kbd {
    background: ${(props) => (props.$isDarkMode ? "#1a1d23" : "#f3f4f6")};
    padding: 2px 6px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 11px;
    border: 1px solid ${(props) => (props.$isDarkMode ? "#4b5563" : "#d1d5db")};
  }
`;

const ModeBadge = styled.div<{ $isEditMode: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 12px;
  color: white;
  background: ${(p) => (p.$isEditMode ? "#10b981" : "#6b7280")};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const Dot = styled.span<{ $isEditMode: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 0 0 3px ${(p) => (p.$isEditMode ? "rgba(16,185,129,0.35)" : "rgba(107,114,128,0.35)")};
`;

const nodeTypes: NodeTypes = {
  serviceNode: ServiceNodeComponent,
  convoyNode: ConvoyNodeComponent,
};

const edgeTypes: EdgeTypes = {
  animated: AnimatedEdge,
};

const DiagramEditorContent: React.FC = () => {
  const { currentDiagram, isEditMode, addConnection, updateNode, updateConnection, saveDiagram, toggleEditMode, selectNode, selectConnection, deleteNode, deleteConnection, selectedNodeId, selectedConnectionId } = useDiagramStore();
  const { isDarkMode } = useTheme();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [showHelp, setShowHelp] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [nodeToolbar, setNodeToolbar] = React.useState<{
    visible: boolean;
    position: { x: number; y: number };
    nodeId: string | null;
  }>({ visible: false, position: { x: 0, y: 0 }, nodeId: null });
  const [edgeToolbar, setEdgeToolbar] = React.useState<{
    visible: boolean;
    position: { x: number; y: number };
    edgeId: string | null;
  }>({ visible: false, position: { x: 0, y: 0 }, edgeId: null });
  const [selectedEdgeSnapshot, setSelectedEdgeSnapshot] = React.useState<Edge | null>(null);

  // Sync nodes and edges with store
  useEffect(() => {
    if (currentDiagram) {
      const flowNodes: Node[] = currentDiagram.nodes.map((node) => ({
        id: node.id,
        type: node.type === "convoy" ? "convoyNode" : "serviceNode",
        position: node.position,
        data: node,
      }));

      const flowEdges: Edge[] = currentDiagram.connections.map((conn) => ({
        id: conn.id,
        source: conn.source,
        target: conn.target,
        sourceHandle: conn.sourceHandle,
        targetHandle: conn.targetHandle,
        type: "animated",
        data: {
          label: conn.label,
          type: conn.type,
          animated: conn.animated,
          convoyRouteId: conn.convoyRouteId,
          curveOffset: conn.curveOffset,
        },
      }));

      setNodes(flowNodes);
      setEdges(flowEdges);
    }
  }, [currentDiagram, setNodes, setEdges]);

  // Decide closest handles given source/target positions
  const pickClosestHandles = useCallback(
    (sourceId: string, targetId: string, existingSourceHandle?: string): { sourceHandle?: string; targetHandle?: string } => {
      // Preserve convoy route source handle if present
      if (existingSourceHandle && existingSourceHandle.startsWith("route-")) {
        // choose closest target side only
        const sourceNode = nodes.find((n) => n.id === sourceId);
        const targetNode = nodes.find((n) => n.id === targetId);
        if (!sourceNode || !targetNode) return { sourceHandle: existingSourceHandle };
        const dx = (targetNode.position?.x ?? 0) - (sourceNode.position?.x ?? 0);
        const dy = (targetNode.position?.y ?? 0) - (sourceNode.position?.y ?? 0);
        if (Math.abs(dx) >= Math.abs(dy)) {
          return {
            sourceHandle: existingSourceHandle,
            targetHandle: dx >= 0 ? "target-left" : "target-right",
          };
        }
        return {
          sourceHandle: existingSourceHandle,
          targetHandle: dy >= 0 ? "target-top" : "target-bottom",
        };
      }

      const sourceNode = nodes.find((n) => n.id === sourceId);
      const targetNode = nodes.find((n) => n.id === targetId);
      if (!sourceNode || !targetNode) return {};

      const dx = (targetNode.position?.x ?? 0) - (sourceNode.position?.x ?? 0);
      const dy = (targetNode.position?.y ?? 0) - (sourceNode.position?.y ?? 0);
      if (Math.abs(dx) >= Math.abs(dy)) {
        return {
          sourceHandle: dx >= 0 ? "source-right" : "source-left",
          targetHandle: dx >= 0 ? "target-left" : "target-right",
        };
      }
      return {
        sourceHandle: dy >= 0 ? "source-bottom" : "source-top",
        targetHandle: dy >= 0 ? "target-top" : "target-bottom",
      };
    },
    [nodes]
  );

  const onConnect = useCallback(
    (params: Connection) => {
      if (!isEditMode) {
        toast.error("Enable edit mode to add connections");
        return;
      }

      if (params.source && params.target) {
        // Check if this is a convoy route connection
        const isConvoyRoute = params.sourceHandle?.startsWith("route-");

        const chosen = pickClosestHandles(params.source, params.target, params.sourceHandle);

        const newConnection: Omit<ServiceConnection, "id"> = {
          source: params.source,
          target: params.target,
          sourceHandle: params.sourceHandle || chosen.sourceHandle || undefined,
          targetHandle: params.targetHandle || chosen.targetHandle || undefined,
          type: isConvoyRoute ? "convoy-route" : "direct",
          animated: isConvoyRoute,
        };

        addConnection(newConnection);
        toast.success("Connection added");
      }
    },
    [isEditMode, addConnection, pickClosestHandles]
  );

  const onNodeDragStop = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (!isEditMode) return;

      updateNode(node.id, {
        position: node.position,
      });

      // After node moves, re-choose closest handles for all connected edges
      const diagram = currentDiagram;
      if (!diagram) return;
      diagram.connections.forEach((conn) => {
        if (conn.source === node.id || conn.target === node.id) {
          const chosen = pickClosestHandles(conn.source, conn.target, conn.sourceHandle);
          const updates: Partial<ServiceConnection> = {};

          // Preserve convoy route source handle and manually pinned source handles (source-*)
          const isPinnedSource = Boolean(conn.sourceHandle && (conn.sourceHandle.startsWith("source-") || conn.sourceHandle.startsWith("route-")));
          const isPinnedTarget = Boolean(conn.targetHandle && conn.targetHandle.startsWith("target-"));

          if (!isPinnedSource && chosen.sourceHandle && chosen.sourceHandle !== conn.sourceHandle) {
            updates.sourceHandle = chosen.sourceHandle;
          }
          if (!isPinnedTarget && chosen.targetHandle && chosen.targetHandle !== conn.targetHandle) {
            updates.targetHandle = chosen.targetHandle;
          }

          if (Object.keys(updates).length > 0) {
            updateConnection(conn.id, updates);
          }
        }
      });
    },
    [isEditMode, updateNode, currentDiagram, pickClosestHandles, updateConnection]
  );

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (!isEditMode) return;

      selectNode(node.id);

      // Calculate toolbar position relative to the node
      const rect = (event.target as HTMLElement).closest(".react-flow__node")?.getBoundingClientRect();
      if (rect) {
        setNodeToolbar({
          visible: true,
          position: {
            x: rect.right + 10, // Position to the right of the node
            y: rect.top - 10,
          },
          nodeId: node.id,
        });
      }

      // Hide edge toolbar if visible
      setEdgeToolbar((prev) => ({ ...prev, visible: false }));
    },
    [selectNode, isEditMode]
  );

  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (!isEditMode) return;

      selectConnection(edge.id);
      setSelectedEdgeSnapshot(edge);

      // Calculate toolbar position relative to mouse click
      setEdgeToolbar({
        visible: true,
        position: {
          x: event.clientX + 10,
          y: event.clientY - 10,
        },
        edgeId: edge.id,
      });

      // Hide node toolbar if visible
      setNodeToolbar((prev) => ({ ...prev, visible: false }));
    },
    [selectConnection, isEditMode]
  );

  // Hide toolbars when clicking on empty space
  const onPaneClick = useCallback(() => {
    setNodeToolbar((prev) => ({ ...prev, visible: false }));
    setEdgeToolbar((prev) => ({ ...prev, visible: false }));
    selectNode(null);
    selectConnection(null);
  }, [selectNode, selectConnection]);

  // Listen for sidebar open events
  useEffect(() => {
    const handleOpenSidebar = () => {
      setSidebarOpen(true);
    };

    document.addEventListener("openSidebar", handleOpenSidebar);
    return () => document.removeEventListener("openSidebar", handleOpenSidebar);
  }, []);

  // Handle keyboard shortcuts for deletion
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Check if Delete or Backspace key is pressed
      if ((event.key === "Delete" || event.key === "Backspace") && isEditMode) {
        // Prevent default browser behavior
        event.preventDefault();

        if (selectedNodeId) {
          // Confirm before deleting node
          if (window.confirm("Are you sure you want to delete this service?")) {
            deleteNode(selectedNodeId);
            toast.success("Service deleted");
          }
        } else if (selectedConnectionId) {
          // Delete connection without confirmation
          deleteConnection(selectedConnectionId);
          toast.success("Connection deleted");
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isEditMode, selectedNodeId, selectedConnectionId, deleteNode, deleteConnection]);

  const handleSave = async () => {
    await saveDiagram();
    toast.success("Diagram saved successfully");
  };

  return (
    <EditorContainer $isDarkMode={isDarkMode} $isEditMode={isEditMode}>
      <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} onNodeDragStop={onNodeDragStop} onNodeClick={onNodeClick} onEdgeClick={onEdgeClick} onPaneClick={onPaneClick} nodeTypes={nodeTypes} edgeTypes={edgeTypes} fitView attributionPosition="bottom-left">
        <Panel position="top-left">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ToolbarButton onClick={toggleEditMode} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              {isEditMode ? <Edit2 size={18} /> : <Eye size={18} />}
              {isEditMode ? "Edit Mode" : "View Mode"}
            </ToolbarButton>

            <ToolbarButton
              onClick={() => {
                if (!isEditMode) {
                  toast.error("Enable edit mode to add services");
                  return;
                }
                setSidebarOpen(true);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus size={18} />
              Add Service
            </ToolbarButton>

            <ToolbarButton onClick={handleSave} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Save size={18} />
              Save
            </ToolbarButton>

            <ToolbarButton
              onClick={async () => {
                await navigator.clipboard.writeText(generateDiagramPrompt);
                toast.success("LLM prompt copied");
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Copy LLM Prompt
            </ToolbarButton>

            <ToolbarButton onClick={() => setShowHelp(!showHelp)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} style={{ marginLeft: "auto" }}>
              <HelpCircle size={18} />
            </ToolbarButton>
          </div>
        </Panel>

        <Panel position="top-right">
          <ModeBadge $isEditMode={isEditMode} title={isEditMode ? "Edit mode: changes will be saved" : "View mode: changes are disabled"}>
            <Dot $isEditMode={isEditMode} />
            {isEditMode ? "Editing" : "Viewing"}
          </ModeBadge>
        </Panel>

        {showHelp && (
          <HelpPanel $isDarkMode={isDarkMode}>
            <h4>
              <HelpCircle size={14} />
              Keyboard Shortcuts
            </h4>
            <div>
              <span>Delete Service/Connection</span>
              <kbd>Del</kbd>
            </div>
            <div>
              <span>Select Service</span>
              <span>Click</span>
            </div>
            <div>
              <span>Connect Services</span>
              <span>Drag Handle</span>
            </div>
            <div>
              <span>Move Service</span>
              <span>Drag Node</span>
            </div>
          </HelpPanel>
        )}

        <Controls />
        <MiniMap
          nodeColor={(node) => {
            const data = node.data as ServiceNode;
            const colors: Record<string, string> = {
              main: "#5e72e4",
              internal: "#11cdef",
              external: "#f5365c",
              convoy: "#fb6340",
            };
            return colors[data.type] || "#11cdef";
          }}
          style={{
            backgroundColor: "rgba(255,255,255,0.9)",
            borderRadius: 8,
            border: "1px solid #e9ecef",
          }}
        />
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
      </ReactFlow>

      {/* Floating toolbars */}
      <FloatingToolbar
        position={nodeToolbar.position}
        visible={nodeToolbar.visible && isEditMode}
        type="node"
        onDelete={() => {
          if (nodeToolbar.nodeId) {
            if (window.confirm("Are you sure you want to delete this service?")) {
              deleteNode(nodeToolbar.nodeId);
              setNodeToolbar((prev) => ({ ...prev, visible: false }));
              toast.success("Service deleted");
            }
          }
        }}
        onEdit={() => {
          if (nodeToolbar.nodeId) {
            selectNode(nodeToolbar.nodeId);
            setNodeToolbar((prev) => ({ ...prev, visible: false }));
            // Open sidebar for editing
            setSidebarOpen(true);
          }
        }}
      />

      <FloatingToolbar
        position={edgeToolbar.position}
        visible={edgeToolbar.visible && isEditMode}
        type="edge"
        onNudgeSourceHandle={(dir) => {
          if (!selectedEdgeSnapshot) return;
          const handle = `source-${dir}` as const;
          updateConnection(selectedEdgeSnapshot.id, { sourceHandle: handle });
        }}
        onNudgeTargetHandle={(dir) => {
          if (!selectedEdgeSnapshot) return;
          const handle = `target-${dir}` as const;
          updateConnection(selectedEdgeSnapshot.id, { targetHandle: handle });
        }}
        sourceName={(() => {
          if (!selectedEdgeSnapshot) return undefined;
          const src = nodes.find((n) => n.id === selectedEdgeSnapshot.source);
          return (src?.data as ServiceNode | undefined)?.name;
        })()}
        targetName={(() => {
          if (!selectedEdgeSnapshot) return undefined;
          const tgt = nodes.find((n) => n.id === selectedEdgeSnapshot.target);
          return (tgt?.data as ServiceNode | undefined)?.name;
        })()}
        onDelete={() => {
          if (edgeToolbar.edgeId) {
            deleteConnection(edgeToolbar.edgeId);
            setEdgeToolbar((prev) => ({ ...prev, visible: false }));
            toast.success("Connection deleted");
          }
        }}
      />

      {/* Sidebar for adding/editing services */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
    </EditorContainer>
  );
};

export const DiagramEditor: React.FC = () => {
  return (
    <ReactFlowProvider>
      <DiagramEditorContent />
    </ReactFlowProvider>
  );
};
